import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  Paper,
  Box,
  Divider,
  Typography,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Tooltip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloudIcon from '@mui/icons-material/Cloud';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SearchResult } from '../../types';
import { useConfig } from '../../contexts/ConfigContext';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  onServerSearch?: (value: string) => Promise<SearchResult[]>;
  onResultSelect?: (result: SearchResult) => void;
  results?: SearchResult[];
  serverResults?: SearchResult[];
  loading?: boolean;
  serverSearchLoading?: boolean;
  requireEnter?: boolean;
  placeholder?: string;
  showServerSearch?: boolean;
  localResultsCount?: number;
  totalResultsCount?: number;
  // Grouped action props
  actions?: {
    onRefresh?: () => void;
    refreshDisabled?: boolean;
    onClear?: () => void;
    additionalButtons?: React.ReactNode;
  };
}





const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSearch,
  onServerSearch,
  onResultSelect,
  results = [],
  serverResults = [],
  loading = false,
  serverSearchLoading = false,
  requireEnter = false,
  placeholder = "Search items...",
  showServerSearch = false,
  localResultsCount,
  totalResultsCount,
  actions
}) => {
  const config = useConfig();
  
  // Extract actions with defaults
  const { 
    onRefresh, 
    refreshDisabled = false, 
    onClear, 
    additionalButtons 
  } = actions || {};
  const [showResults, setShowResults] = useState(false);
  const [hasSearchedServer, setHasSearchedServer] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);
  
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onSearch) {
      onSearch(value);
    }
  };

  const handleChange = (newValue: string) => {
    onChange(newValue);
    setShowResults(true);
    setHasSearchedServer(false); // Reset server search state
    if (!requireEnter && onSearch) {
      onSearch(newValue);
    }
  };

  const handleServerSearch = async () => {
    if (onServerSearch && value.trim()) {
      setHasSearchedServer(true);
      try {
        await onServerSearch(value.trim());
      } catch (error) {
        console.error('Server search failed:', error);
      }
    }
  };

  const handleRefresh = () => {
    if (onClear) {
      onClear(); // Clear the search term
    }
    if (onRefresh) {
      onRefresh(); // Refresh the data
    }
  };

  // Combine local and server results
  const combinedResults = [...results, ...serverResults];
  const hasLocalResults = results.length > 0;
  const hasServerResults = serverResults.length > 0;
  
  // Check if server search should be shown - requires Places endpoint to exist
  const hasPlacesEndpoint = config?.apiEndpoints?.some(endpoint => endpoint.tableName === 'places');
  const showInlineServerButton = showServerSearch && hasPlacesEndpoint && value.trim().length > 0 && !hasSearchedServer && !serverSearchLoading;

  const handleResultClick = (result: SearchResult) => {
    if (onResultSelect) {
      onResultSelect(result);
      setShowResults(false);
    }
  };

  // Highlight the matched part of text
  const highlightMatch = (text: string, matchText: string) => {
    if (!matchText || !text.includes(matchText)) return text;
    
    const parts = text.split(new RegExp(`(${matchText})`, 'i'));
    
    return parts.map((part, i) => 
      part.toLowerCase() === matchText.toLowerCase() ? 
        <Box component="span" key={i} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '2px', padding: '0 2px' }}>
          {part}
        </Box> : part
    );
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={searchBarRef}>
      <Paper 
        elevation={0} 
        sx={{ 
          mb: results.length > 0 && showResults ? 0 : 3, 
          p: 2, 
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: results.length > 0 && showResults ? '4px 4px 0 0' : 1,
          position: 'relative',
          zIndex: 100,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Refresh Button - Left side */}
          {onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton 
                onClick={handleRefresh}
                disabled={refreshDisabled}
                sx={{
                  color: 'white',
                  backgroundColor: 'primary.main',
                  border: '1px solid primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    borderColor: 'primary.dark',
                  },
                  '&:disabled': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Search Field with inline GET MORE button */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, position: 'relative' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={placeholder}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {/* Inline Server Search Button - appears first (leftmost) */}
                    {showInlineServerButton && (
                      <Button
                        variant="contained"
                        startIcon={<CloudIcon />}
                        onClick={handleServerSearch}
                        disabled={serverSearchLoading}
                        size="small"
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          mr: 1,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        Get More
                      </Button>
                    )}
                    
                    {/* Server Search Loading - appears after button */}
                    {serverSearchLoading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', whiteSpace: 'nowrap' }}>
                          Searching...
                        </Typography>
                      </Box>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputAdornment-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              }}
            />
          </Box>
          
          {/* Additional Action Buttons */}
          {additionalButtons}
        </Box>
      </Paper>
      
      {(combinedResults.length > 0) && showResults && (
        <Paper
          elevation={2}
          sx={{
            mb: 3,
            maxHeight: '400px',
            overflow: 'auto',
            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            borderRadius: '0 0 4px 4px',
            zIndex: 99,
          }}
        >
          <Box sx={{ py: 1 }}>
            {/* Local Results Section */}
            {hasLocalResults && (
              <>
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>
                    LOADED ITEMS
                    {localResultsCount && totalResultsCount && (
                      <span style={{ fontWeight: 'normal' }}>
                        {' '}({localResultsCount} of {totalResultsCount} searched)
                      </span>
                    )}
                  </Typography>
                </Box>
                {results.map((result, index) => (
                  <React.Fragment key={`local-${result.type}-${result.id}`}>
                    <Box
                      onClick={() => handleResultClick(result)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'background-color 0.2s ease',
                        padding: '8px 16px',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Result name and type */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {highlightMatch(result.name, value)}
                        </Typography>
                        <Chip 
                          label={result.type === 'coordinate' ? 'Coordinates' : result.type}
                          size="small"
                          sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.7rem',
                            height: '20px'
                          }}
                        />
                      </Box>
                      
                      {/* Display which fields matched */}
                      {result.properties?.matchedFields && (
                        <Box sx={{ mt: 0.5 }}>
                          {result.properties.matchedFields.map((match: any, i: number) => (
                            <Box
                              key={`match-${i}`}
                              sx={{
                                fontSize: '0.8rem',
                                color: 'rgba(255, 255, 255, 0.6)',
                                mb: 0.3,
                                display: 'flex',
                              }}
                            >
                              <Typography component="span" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.7)', minWidth: '70px', textTransform: 'capitalize' }}>
                                {match.field.replace('_', ' ')}:
                              </Typography>
                              <Typography component="span" sx={{ ml: 1 }}>
                                {highlightMatch(match.value, match.match)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                    {index < results.length - 1 && (
                      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                    )}
                  </React.Fragment>
                ))}
              </>
            )}





            {/* Server Results Section */}
            {hasServerResults && (
              <>
                {hasLocalResults && <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />}
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>
                    SERVER RESULTS ({serverResults.length} found)
                  </Typography>
                </Box>
                {serverResults.map((result, index) => (
                  <React.Fragment key={`server-${result.type}-${result.id}`}>
                    <Box
                      onClick={() => handleResultClick(result)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'background-color 0.2s ease',
                        padding: '8px 16px',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Result name and type */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {highlightMatch(result.name, value)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip 
                            label={result.type === 'coordinate' ? 'Coordinates' : result.type}
                            size="small"
                            sx={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontSize: '0.7rem',
                              height: '20px'
                            }}
                          />
                          <CloudIcon sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }} />
                        </Box>
                      </Box>
                      
                      {/* Display which fields matched */}
                      {result.properties?.matchedFields && (
                        <Box sx={{ mt: 0.5 }}>
                          {result.properties.matchedFields.map((match: any, i: number) => (
                            <Box
                              key={`match-${i}`}
                              sx={{
                                fontSize: '0.8rem',
                                color: 'rgba(255, 255, 255, 0.6)',
                                mb: 0.3,
                                display: 'flex',
                              }}
                            >
                              <Typography component="span" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.7)', minWidth: '70px', textTransform: 'capitalize' }}>
                                {match.field.replace('_', ' ')}:
                              </Typography>
                              <Typography component="span" sx={{ ml: 1 }}>
                                {highlightMatch(match.value, match.match)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                    {index < serverResults.length - 1 && (
                      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                    )}
                  </React.Fragment>
                ))}
              </>
            )}

            {/* No Results Message */}
            {hasSearchedServer && !hasLocalResults && !hasServerResults && !serverSearchLoading && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No results found for "{value}"
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
      
      {loading && value && !showResults && (
        <Box 
          sx={{ 
            color: 'white', 
            ml: 2, 
            mt: 1, 
            fontWeight: 'normal',
            fontSize: '0.875rem'
          }}
        >
          Searching...
        </Box>
      )}
    </div>
  );
};

export default SearchBar; 